/**
 * Treatment × Area landing page — e.g. /invisalign-ealing, /dental-implants-chiswick
 * Unique per combination: quick answer, local angle, transport, prices, process, FAQs, schema.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, MapPin, Train, CheckCircle2, ArrowRight, Clock, PoundSterling } from 'lucide-react';
import SEO from './SEO';
import PageHeader from './PageHeader';
import SiteFooter from './SiteFooter';
import Breadcrumbs from './Breadcrumbs';
import DirectAnswer from './DirectAnswer';
import NotFound from '../pages/NotFound';
import CaseGrid from './gallery/CaseGrid';
import { Button } from './ui/button';
import { PUBLIC_SITE_URL } from '../data/seo';
import { PRACTICE, WHATSAPP_LINK } from '../data/practice';
import { resolveTreatmentArea, treatments, TREATMENT_AREA_SLUGS, nearestAreas } from '../data/treatmentAreas';
import { getLocalCopy } from '../data/local';
import { enquiryPath } from '../data/enquiry';
import { getTreatmentAreaLD, getDentistClinicLD, getPersonClinicianLD, getBreadcrumbLD, getFAQPageLD, getSpeakableLD } from '../data/structuredData';

const TreatmentAreaPage = () => {
  const { pathname } = useLocation();
  const match = resolveTreatmentArea(pathname);
  if (!match) return <NotFound />;
  const { treatment: t, area } = match;
  const copy = getLocalCopy(area.slug, t.slug);
  if (!copy) return <NotFound />;
  const path = `/${t.slug}-${area.slug}`;
  // Two area-specific questions, then the shared clinical ones.
  const faqs = [...copy.faqs, ...t.faqs];
  const siblings = TREATMENT_AREA_SLUGS.filter((s) => s !== t.slug).map((s) => treatments[s]);
  const nearby = nearestAreas(area, 3);
  const crumbs = [{ name: 'Home', to: '/' }, { name: `Dentist ${area.name}`, to: `/dentist-${area.slug}` }, { name: `${t.name} ${area.name}` }];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={t.title(area)}
        description={copy.description}
        path={path}
        keywords={[`${t.name.toLowerCase()} ${area.name.toLowerCase()}`, `${t.name.toLowerCase()} ${area.postcode.toLowerCase()}`, `${t.name.toLowerCase()} near ${area.name.toLowerCase()}`, `${t.noun} west london`, `dentist ${area.name.toLowerCase()}`]}
        structuredData={[
          getTreatmentAreaLD({ treatment: t, area, path, description: copy.description }),
          getDentistClinicLD(),
          getPersonClinicianLD(),
          getBreadcrumbLD([{ name: 'Home', url: `${PUBLIC_SITE_URL}/` }, { name: `Dentist ${area.name}`, url: `${PUBLIC_SITE_URL}/dentist-${area.slug}` }, { name: `${t.name} ${area.name}`, url: `${PUBLIC_SITE_URL}${path}` }]),
          getFAQPageLD(faqs),
          getSpeakableLD([copy.quickAnswer.slice(0, 200)]),
        ]}
      />
      <PageHeader />

      <main className="pt-28 md:pt-32">
        <section className="container mx-auto px-4 max-w-5xl">
          <Breadcrumbs items={crumbs} />
          <div className="inline-flex items-center gap-2 bg-sky-gray/10 px-4 py-2 rounded-full mt-6 mb-4">
            <MapPin className="w-4 h-4 text-sky-gray" aria-hidden="true" />
            <span className="text-sm font-medium text-burnt-coral">Serving {area.name} {area.postcode} · {area.distanceMinutes} min from {area.tubes[0]}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-rabbit leading-tight mb-6" data-testid="ta-h1">{t.h1(area)}</h1>
          <DirectAnswer question={`${t.name} for ${area.name} patients — the essentials`} answer={copy.quickAnswer} bullets={t.bullets} />

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button asChild size="lg" className="bg-burnt-coral hover:bg-burnt-coral/90 text-white"><a href={PRACTICE.phoneTel} data-testid="ta-call-btn"><Phone className="w-5 h-5 mr-2" />Call {PRACTICE.phoneDisplay}</a></Button>
            <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#20BA5A] text-white"><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" data-testid="ta-whatsapp-btn">WhatsApp for a free consultation</a></Button>
            <Button asChild size="lg" variant="outline"><Link to={enquiryPath(t.slug, t.name)} data-testid="ta-enquire-link">Enquire online</Link></Button>
          </div>

          <div className="grid sm:grid-cols-3 gap-4 mt-8 text-sm">
            <div className="rounded-xl border border-sky-gray/20 p-4 flex gap-3"><PoundSterling className="w-5 h-5 text-[#01A9B4] flex-shrink-0" aria-hidden="true" /><div><p className="font-semibold text-rabbit">From {t.priceFrom}</p><p className="text-chocolate-malt">{t.monthly}</p></div></div>
            <div className="rounded-xl border border-sky-gray/20 p-4 flex gap-3"><Clock className="w-5 h-5 text-[#01A9B4] flex-shrink-0" aria-hidden="true" /><div><p className="font-semibold text-rabbit">Typical timeline</p><p className="text-chocolate-malt">{t.duration}</p></div></div>
            <div className="rounded-xl border border-sky-gray/20 p-4 flex gap-3"><Train className="w-5 h-5 text-[#01A9B4] flex-shrink-0" aria-hidden="true" /><div><p className="font-semibold text-rabbit">{area.distanceMinutes} min from {area.name}</p><p className="text-chocolate-malt">via {area.tubes.join(' or ')}</p></div></div>
          </div>
        </section>

        <section className="container mx-auto px-4 max-w-5xl mt-16 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">Why {area.name} patients choose us for {t.noun}</h2>
            <p className="text-chocolate-malt leading-relaxed mb-4">{copy.opener}</p>
            <p className="text-chocolate-malt leading-relaxed">{copy.why}</p>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">Benefits of {t.noun}</h2>
            <ul className="space-y-3">
              {t.benefits.map((b) => <li key={b} className="flex gap-3 text-chocolate-malt"><CheckCircle2 className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span>{b}</span></li>)}
            </ul>
          </div>
        </section>

        <section className="bg-sky-gray/10 py-16 mt-16">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">{t.name} step by step</h2>
            <ol className="grid md:grid-cols-5 gap-4">
              {t.steps.map((s, i) => (
                <li key={s.title} className="bg-white rounded-xl p-5 border border-sky-gray/20">
                  <span className="w-8 h-8 rounded-full bg-burnt-coral text-white font-bold flex items-center justify-center mb-3" aria-hidden="true">{i + 1}</span>
                  <h3 className="font-bold text-rabbit mb-1">{s.title}</h3>
                  <p className="text-sm text-chocolate-malt">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="container mx-auto px-4 max-w-5xl mt-16 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">{t.name} prices for {area.name} patients</h2>
            <table className="w-full text-sm border border-sky-gray/20 rounded-xl overflow-hidden" data-testid="ta-price-table">
              <thead className="bg-sky-gray/10 text-rabbit"><tr><th scope="col" className="text-left px-4 py-2">Treatment</th><th scope="col" className="text-left px-4 py-2">Price</th></tr></thead>
              <tbody>{t.prices.map(([k, v]) => <tr key={k} className="border-t border-sky-gray/15"><td className="px-4 py-2 text-chocolate-malt">{k}</td><td className="px-4 py-2 font-semibold text-[#006A71] whitespace-nowrap">{v}</td></tr>)}</tbody>
            </table>
            <p className="text-xs text-chocolate-malt/70 mt-2">Guide prices; written quote after examination. 0% finance over 3–60 months on treatment over £500. <Link to="/finance" className="underline">Finance calculator</Link>.</p>
          </div>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">Getting here from {area.name}</h2>
            <ul className="space-y-3 text-chocolate-malt">
              <li className="flex gap-3"><Train className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span>From <strong>{area.tubes[0]}</strong>{area.tubes[1] ? ` or ${area.tubes[1]}` : ''}: about {area.distanceMinutes} minutes to Acton Main Line (Elizabeth line) or Acton Central, then a 4–9 minute walk to 64a Horn Lane.</span></li>
              <li className="flex gap-3"><MapPin className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span>{PRACTICE.addressOneLine}. {PRACTICE.parking}</span></li>
              <li className="flex gap-3"><Clock className="w-5 h-5 text-[#01A9B4] flex-shrink-0 mt-0.5" aria-hidden="true" /><span>Open Mon–Fri 9:00–18:00, Sat 9:00–14:00. Early and Saturday slots are popular with {area.name} commuters — book ahead.</span></li>
            </ul>
            <Link to="/contact" className="inline-flex items-center gap-1 text-burnt-coral font-medium mt-4 hover:underline">Full directions & map<ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
          </div>
        </section>

        {/* The section that only makes sense for this treatment in this area */}
        <section className="container mx-auto px-4 max-w-5xl mt-16" data-testid="ta-local-section">
          <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-4">{copy.local.heading}</h2>
          <p className="text-chocolate-malt leading-relaxed max-w-3xl">{copy.local.body}</p>
        </section>

        {t.galleryTreatment && (
          <section className="container mx-auto px-4 max-w-5xl mt-16">
            <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-6">Recent {t.name.toLowerCase()} results</h2>
            <CaseGrid treatment={t.galleryTreatment} limit={3} />
            <Link to="/before-after" className="inline-flex items-center gap-1 text-burnt-coral font-medium mt-4 hover:underline">View the full before &amp; after gallery<ArrowRight className="w-4 h-4" aria-hidden="true" /></Link>
          </section>
        )}

        <section className="container mx-auto px-4 max-w-4xl mt-16">
          <h2 className="text-2xl md:text-3xl font-bold text-rabbit mb-8">{t.name} in {area.name} — your questions answered</h2>
          <div className="space-y-6">
            {faqs.map((f) => (
              <div key={f.question} className="border-b border-sky-gray/20 pb-6">
                <h3 className="text-lg font-bold text-rabbit mb-2">{f.question}</h3>
                <p className="text-chocolate-malt leading-relaxed">{f.answer}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-chocolate-malt/80 mt-6">Clinical content reviewed by <Link to="/about-dr-kartik-patel" className="underline underline-offset-4 text-[#006A71]">Dr Kartik Patel BDS</Link>, GDC {PRACTICE.gdcNumber}.</p>
        </section>

        <section className="bg-sky-gray/10 py-14 mt-16">
          <div className="container mx-auto px-4 max-w-5xl grid md:grid-cols-3 gap-8 text-sm">
            <nav aria-label={`Other treatments for ${area.name} patients`}>
              <h2 className="font-bold text-rabbit mb-3">More for {area.name} patients</h2>
              <ul className="space-y-2">
                <li><Link to={`/dentist-${area.slug}`} className="text-chocolate-malt hover:text-burnt-coral">General dentist near {area.name}</Link></li>
                {siblings.map((s) => <li key={s.slug}><Link to={`/${s.slug}-${area.slug}`} className="text-chocolate-malt hover:text-burnt-coral" data-testid={`ta-sibling-${s.slug}`}>{s.name} {area.name}</Link></li>)}
              </ul>
            </nav>
            <nav aria-label={`${t.name} in nearby areas`}>
              <h2 className="font-bold text-rabbit mb-3">{t.name} near you</h2>
              <ul className="space-y-2">
                <li><Link to={t.hubPath} className="text-chocolate-malt hover:text-burnt-coral">{t.name} Acton (main page)</Link></li>
                {nearby.map((n) => <li key={n.slug}><Link to={`/${t.slug}-${n.slug}`} className="text-chocolate-malt hover:text-burnt-coral">{t.name} {n.name}</Link></li>)}
              </ul>
            </nav>
            <nav aria-label="Learn more">
              <h2 className="font-bold text-rabbit mb-3">Learn more</h2>
              <ul className="space-y-2">
                <li><Link to={t.servicePath} className="text-chocolate-malt hover:text-burnt-coral">In-depth {t.name.toLowerCase()} guide</Link></li>
                <li><Link to="/pricing" className="text-chocolate-malt hover:text-burnt-coral">Full fee guide</Link></li>
                <li><Link to="/reviews" className="text-chocolate-malt hover:text-burnt-coral">Patient reviews</Link></li>
                <li><Link to="/about-dr-kartik-patel" className="text-chocolate-malt hover:text-burnt-coral">About Dr Kartik Patel</Link></li>
              </ul>
            </nav>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default TreatmentAreaPage;
